import React, { useEffect, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import "./Donation.scss";

const PRESET_AMOUNTS = [50, 100, 200, 500];
const MIN_AMOUNT = 5;

const CHANNELS = [
  { id: "mtn", label: "MTN Mobile Money" },
  { id: "telecel", label: "Telecel Cash" },
  { id: "airteltigo", label: "AirtelTigo Money" },
  { id: "card", label: "Debit / Credit Card" },
];

// Single serverless function that talks directly to Moolre (see api/donate.js).
// Set REACT_APP_API_BASE if it's ever deployed somewhere other than /api/donate.
const API_BASE = process.env.REACT_APP_API_BASE || "/api/donate";

function formatGHS(amount) {
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency: "GHS",
  }).format(amount);
}

function Donation() {
  const [step, setStep] = useState("amount"); // amount | details | processing | success | error
  const [amount, setAmount] = useState(100);
  const [customAmount, setCustomAmount] = useState("");
  const [channel, setChannel] = useState("mtn");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const pollRef = useRef(null);

  const finalAmount = customAmount ? Number(customAmount) : amount;

  useEffect(() => {
    return () => clearInterval(pollRef.current);
  }, []);

  function selectPreset(value) {
    setAmount(value);
    setCustomAmount("");
  }

  function goToDetails(e) {
    e.preventDefault();
    if (!finalAmount || finalAmount < MIN_AMOUNT) {
      setErrorMessage(`Please enter at least ${formatGHS(MIN_AMOUNT)}.`);
      return;
    }
    setErrorMessage("");
    setStep("details");
  }

  async function submitDonation(e) {
    e.preventDefault();
    setErrorMessage("");

    if (!name.trim()) return setErrorMessage("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return setErrorMessage("Please enter a valid email.");
    if (channel !== "card" && !/^0\d{9}$/.test(phone)) {
      return setErrorMessage("Please enter a valid Ghana mobile number, e.g. 0244123456.");
    }

    setStep("processing");
    setStatusMessage("Starting your payment...");

    try {
      const res = await fetch(API_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: finalAmount, channel, phone, email, name }),
      });
      const data = await res.json();

      if (!res.ok) {
        setStep("error");
        setErrorMessage(data.error || "Something went wrong starting your payment.");
        return;
      }

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      setStatusMessage(data.message || "Check your phone to approve the payment.");
      pollPaymentStatus(data.reference);
    } catch (err) {
      setStep("error");
      setErrorMessage("We couldn't reach the payment service. Please check your connection and try again.");
    }
  }

  function pollPaymentStatus(reference) {
    let attempts = 0;
    const maxAttempts = 20; // ~60s at 3s intervals

    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const res = await fetch(`${API_BASE}?reference=${encodeURIComponent(reference)}`);
        const data = await res.json();

        if (data.status === "success") {
          clearInterval(pollRef.current);
          setStep("success");
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          setStep("error");
          setErrorMessage("The payment was not completed. You can try again.");
        } else if (attempts >= maxAttempts) {
          clearInterval(pollRef.current);
          setStep("error");
          setErrorMessage("We're still waiting on confirmation. If you approved the prompt, your donation may still go through shortly.");
        }
      } catch {
        // Transient network hiccup during polling - keep trying until maxAttempts.
      }
    }, 3000);
  }

  function startOver() {
    clearInterval(pollRef.current);
    setStep("amount");
    setErrorMessage("");
    setStatusMessage("");
  }

  return (
    <>
      <div className="donations">
        <div className="overlay"></div>
        <div className="content">
          <Navbar />
          <div>
            <h1>Donation</h1>
          </div>
          <div className="extra">
            <br />
          </div>
        </div>
      </div>

      <div className="paymentWrapper">
        { /** 
        <h3>
          
          How much would you like to donate? As a contributor to ASK Ghana we
          make sure your donation goes directly to supporting our cause.
          </h3>
          */}


        <div className="donationCard">
          {step === "amount" && (
            <form onSubmit={goToDetails}>
              <div className="presetAmounts">
                {PRESET_AMOUNTS.map((value) => (
                  <button
                    type="button"
                    key={value}
                    className={`presetBtn ${!customAmount && amount === value ? "active" : ""}`}
                    onClick={() => selectPreset(value)}
                  >
                    {formatGHS(value)}
                  </button>
                ))}
              </div>

              <label className="fieldLabel" htmlFor="customAmount">
                Or enter a custom amount (GHS)
              </label>
              <input
                id="customAmount"
                type="number"
                min={MIN_AMOUNT}
                placeholder="Enter an amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
              />

              {errorMessage && <p className="fieldError">{errorMessage}</p>}

              <button type="submit" className="primaryBtn">
                Continue with {formatGHS(finalAmount || 0)}
              </button>
            </form>
          )}

          {step === "details" && (
            <form onSubmit={submitDonation}>
              <p className="stepSummary">
                Donating {formatGHS(finalAmount)}.{" "}
                <button type="button" className="linkBtn" onClick={() => setStep("amount")}>
                  Change amount
                </button>
              </p>

              <label className="fieldLabel" htmlFor="name">Full name</label>
              <input
                id="name"
                type="text"
                placeholder="Ama Mensah"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              <label className="fieldLabel" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <label className="fieldLabel">Payment method</label>
              <div className="channelGrid">
                {CHANNELS.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className={`channelBtn ${channel === c.id ? "active" : ""}`}
                    onClick={() => setChannel(c.id)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              {channel !== "card" && (
                <>
                  <label className="fieldLabel" htmlFor="phone">Mobile money number</label>
                  <input
                    id="phone"
                    type="tel"
                    placeholder="0244123456"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </>
              )}

              {errorMessage && <p className="fieldError">{errorMessage}</p>}

              <button type="submit" className="primaryBtn">
                Donate {formatGHS(finalAmount)}
              </button>
            </form>
          )}

          {step === "processing" && (
            <div className="statusPanel">
              <div className="spinner" aria-hidden="true"></div>
              <p>{statusMessage}</p>
            </div>
          )}

          {step === "success" && (
            <div className="statusPanel success">
              <h4>Thank you!</h4>
              <p>Your donation of {formatGHS(finalAmount)} was received. ASK Ghana is grateful for your support.</p>
              <button className="primaryBtn" onClick={startOver}>Make another donation</button>
            </div>
          )}

          {step === "error" && (
            <div className="statusPanel error">
              <h4>We hit a snag</h4>
              <p>{errorMessage}</p>
              <button className="primaryBtn" onClick={startOver}>Try again</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Donation;